import {Form, FormField, FormSubmitHandler, Label, Separator} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useParams} from 'react-router-dom';

import {isNil, keysIn} from 'ramda';
import {isNotNilOrEmpty, isNotString, isTrue} from 'ramda-adjunct';

import {
  useCreateInvoiceMarginMutation,
  useGetInvoiceMarginQuery,
  useUpdateInvoiceMarginMutation,
} from '@dms/api/finance';
import {EditInvoiceMarginApiArg} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

type FormValues = z.infer<typeof zodSchema>;

const breadcrumbs = [
  {
    label: i18n.t('page.accountingSettings.labels.invoiceMargin'),
    href: settingsRoutes.invoiceMarginOverview,
  },
];

export function InvoiceMarginDetail() {
  const navigate = useNavigate();
  const {id} = useParams();
  const isCreating = isNotString(id);

  const [updateInvoiceMargin] = useUpdateInvoiceMarginMutation();
  const [createInvoiceMargin] = useCreateInvoiceMarginMutation();

  const {
    data: branches,
    branchOptions,
    isLoading: isLoadingBranches,
    isError: isBranchesError,
  } = useBranches();

  const {
    data: invoiceMargin,
    isLoading: isLoadingInvoiceMargin,
    isError: isInvoiceMarginError,
  } = useGetInvoiceMarginQuery({id: id as string}, {skip: isCreating});

  const isLoading = isLoadingBranches || isLoadingInvoiceMargin;
  const isError = isBranchesError || isInvoiceMarginError;

  const handleOnSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const submitAction = isCreating ? createInvoiceMargin : updateInvoiceMargin;

    await submitAction(data as EditInvoiceMarginApiArg)
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.invoiceMarginOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const getTitle = () => {
    if (isNil(branches) || isNil(invoiceMargin) || isCreating) {
      return i18n.t('page.accounting.labels.newDefinition');
    }

    const {brokerageNotVatDeductible, buyNotVatDeductible, buyVatDeductible} = invoiceMargin;

    const invoiceMarginItems: Record<string, boolean> = {
      brokerageNotVatDeductible,
      buyNotVatDeductible,
      buyVatDeductible,
    };

    if (invoiceMargin.isSystem) {
      return i18n.t('page.accounting.labels.tenantDefinition');
    }

    const branchName = branches.branchListItems.find(
      (branch) => branch.id === invoiceMargin.branchId
    )?.marketingName;

    const filledBusinessCaseType = keysIn(invoiceMarginItems)
      .filter((key) => isTrue(invoiceMarginItems[key]))
      .map((key) => i18n.t(`page.accountingSettings.labels.${key}`))
      .join(', ');

    return `${branchName}${
      isNotNilOrEmpty(filledBusinessCaseType) ? ' - ' : ''
    }${filledBusinessCaseType}`;
  };

  const initialValues = {
    branchId: undefined,
    buyNotVatDeductible: false,
    buyVatDeductible: false,
    brokerageNotVatDeductible: false,
    hideStandardInvoicesWarehouseNotVatDeductible: false,
    hideStandardInvoicesWarehouseVatDeductible: false,
    hideStandardInvoicesBrokerageNotVatDeductible: false,
    isSystem: false,
    ...invoiceMargin,
  };

  return (
    <SettingsTemplate
      header={{
        title: getTitle(),
        breadcrumbs,
      }}
      isCreating={isCreating}
      data-testid={testIds.settings.invoiceMarginDetail('page')}
      isError={isError}
      isLoading={isLoading}
    >
      <Form<FormValues>
        onSubmit={handleOnSubmit}
        defaultValues={initialValues}
        experimentalZodSchema={zodSchema}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => {
          const [brokerageNotVatDeductible, buyNotVatDeductible, buyVatDeductible] = formApi.watch([
            'brokerageNotVatDeductible',
            'buyNotVatDeductible',
            'buyVatDeductible',
          ]);

          const handleUpdateMarginDefinition = (name: keyof FormValues) => (value: boolean) => {
            if (isTrue(value)) {
              return;
            }

            formApi.setValue(name, false);
          };

          return (
            <SettingsSection>
              <VStack spacing={4}>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    isDisabled={invoiceMargin?.isSystem}
                    data-testid={testIds.settings.invoiceMarginDetail('branch')}
                    options={branchOptions}
                    label={i18n.t('entity.cashRegister.parameters.branch')}
                    placeholder={
                      invoiceMargin?.isSystem
                        ? i18n.t('page.accounting.labels.tenantDefinition')
                        : undefined
                    }
                    isRequired
                    isNotClearable
                  />
                </Grid>

                <VStack spacing={1}>
                  <Label isRequired>
                    {i18n.t('page.accountingSettings.labels.businessCaseType')}
                  </Label>
                  <VStack spacing={2}>
                    <Grid columns={2}>
                      <FormField
                        control={control}
                        type="checkbox"
                        name="brokerageNotVatDeductible"
                        onChange={handleUpdateMarginDefinition(
                          'hideStandardInvoicesBrokerageNotVatDeductible'
                        )}
                        label={i18n.t('page.accountingSettings.labels.brokerageNotVatDeductible')}
                        data-testid={testIds.settings.invoiceMarginDetail(
                          'brokerageNotVatDeductible'
                        )}
                      />

                      <FormField
                        control={control}
                        type="checkbox"
                        isDisabled={!brokerageNotVatDeductible}
                        name="hideStandardInvoicesBrokerageNotVatDeductible"
                        label={i18n.t('page.accountingSettings.labels.showMarginInvoice')}
                        data-testid={testIds.settings.invoiceMarginDetail('showMarginInvoice')}
                      />
                    </Grid>
                    <Separator spacing={0} />

                    <Grid columns={2}>
                      <FormField
                        control={control}
                        type="checkbox"
                        name="buyNotVatDeductible"
                        onChange={handleUpdateMarginDefinition(
                          'hideStandardInvoicesWarehouseNotVatDeductible'
                        )}
                        label={i18n.t('page.accountingSettings.labels.buyNotVatDeductible')}
                        data-testid={testIds.settings.invoiceMarginDetail('buyNotVatDeductible')}
                      />

                      <FormField
                        control={control}
                        type="checkbox"
                        isDisabled={!buyNotVatDeductible}
                        name="hideStandardInvoicesWarehouseNotVatDeductible"
                        label={i18n.t('page.accountingSettings.labels.showMarginInvoice')}
                        data-testid={testIds.settings.invoiceMarginDetail('showMarginInvoice')}
                      />
                    </Grid>

                    <Separator spacing={0} />

                    <Grid columns={2}>
                      <FormField
                        control={control}
                        type="checkbox"
                        name="buyVatDeductible"
                        onChange={handleUpdateMarginDefinition(
                          'hideStandardInvoicesWarehouseVatDeductible'
                        )}
                        label={i18n.t('page.accountingSettings.labels.buyVatDeductible')}
                        data-testid={testIds.settings.invoiceMarginDetail('buyVatDeductible')}
                      />

                      <FormField
                        control={control}
                        type="checkbox"
                        isDisabled={!buyVatDeductible}
                        name="hideStandardInvoicesWarehouseVatDeductible"
                        label={i18n.t('page.accountingSettings.labels.showMarginInvoice')}
                        data-testid={testIds.settings.invoiceMarginDetail('showMarginInvoice')}
                      />
                    </Grid>
                  </VStack>
                </VStack>

                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.discard'),
                      onClick: () => navigateWithoutBlocker(settingsRoutes.invoiceMarginOverview),
                      variant: 'secondary',
                      'data-testid': testIds.settings.invoiceMarginDetail('discard'),
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: isCreating
                        ? i18n.t('general.actions.create')
                        : i18n.t('general.actions.save'),
                      'data-testid': testIds.settings.invoiceMarginDetail('save'),
                    },
                  ]}
                />
              </VStack>
            </SettingsSection>
          );
        }}
      </Form>
    </SettingsTemplate>
  );
}

const zodSchema = z
  .object({
    isSystem: z.boolean(),
    id: z.string().nullish(),
    branchId: z.string().nullish(),
    buyNotVatDeductible: z.boolean(),
    buyVatDeductible: z.boolean(),
    brokerageNotVatDeductible: z.boolean(),
    hideStandardInvoicesWarehouseNotVatDeductible: z.boolean(),
    hideStandardInvoicesWarehouseVatDeductible: z.boolean(),
    hideStandardInvoicesBrokerageNotVatDeductible: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.isSystem) {
        return true;
      }
      return isNotNilOrEmpty(data.branchId);
    },
    {
      message: i18n.t('general.validations.fieldIsRequired'),
      path: ['branchId'],
    }
  );
